// src/services/locksClient.js
// Cliente utilitário para o bloqueio de edição usando /api/locks
// Agora o sessionId via BODY (POST) ou QUERY (GET), sem usar headers customizados.

import axios from 'axios'

const DEFAULT_LOCK_TTL_MS = 2 * 60 * 1000     // 2min (apenas para UX do front)
const DEFAULT_HEARTBEAT_MS = 65 * 1000        // 65s para renovar

// ========= sessionId por documento/aba =========
export function getOrCreateSessionIdForResource(resource) {
    const key = `sessionId:${resource}`
    let sid = sessionStorage.getItem(key)
    if (!sid) {
        // UUID v4 simples (sem deps)
        sid = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        )
        sessionStorage.setItem(key, sid)
    }
    return sid
}

// ========= Chamadas de API (sessionId no body/params) =========
export async function acquireLock({ resource, sessionId }) {
    const body = { resource, sessionId }
    try {
        const r = await axios.post('/api/locks/acquire', body)
        return { ok: true, data: r.data }
    } catch (err) {
        const resp = err?.response
        if (resp?.status === 423) {
            return {
                ok: false,
                locked: true,
                by: resp?.data?.by || null,
                expiresAt: resp?.data?.expiresAt || null,
                waitMs: resp?.data?.waitMs ?? null
            }
        }
        throw err
    }
}

export async function renewLock({ resource, sessionId }) {
    const body = { resource, sessionId }
    try {
        const r = await axios.post('/api/locks/renew', body)
        return { ok: true, data: r.data }
    } catch (err) {
        const resp = err?.response
        if (resp?.status === 423) {
            return {
                ok: false,
                locked: true,
                by: resp?.data?.by || null,
                expiresAt: resp?.data?.expiresAt || null,
                waitMs: resp?.data?.waitMs ?? null
            }
        }
        throw err
    }
}

export async function releaseLock({ resource, sessionId }) {
    const body = { resource, sessionId }
    try {
        await axios.post('/api/locks/release', body)
        return { ok: true }
    } catch {
        return { ok: false }
    }
}

export async function statusLock({ tipo, id, sessionId }) {
    // sessionId como query param (para futuro uso no backend, se desejar)
    const url = `/api/locks/status/${encodeURIComponent(tipo)}/${encodeURIComponent(id)}`
    const r = await axios.get(url, { params: { sessionId } })
    return r.data
}

// ========= Heartbeat (renovação periódica) =========
export function startLockHeartbeat({
    resource,
    sessionId,
    intervalMs = DEFAULT_HEARTBEAT_MS,
    onRenewOk,
    onRenewFail
}) {
    let timer = null
    let stopped = false

    async function tick() {
        if (stopped) return
        try {
            const r = await renewLock({ resource, sessionId })
            if (r.ok) {
                onRenewOk && onRenewOk(r.data)
            } else {
                onRenewFail && onRenewFail(r) // { ok:false, locked:true, ... }
            }
        } catch (err) {
            onRenewFail && onRenewFail(err)
        } finally {
            if (!stopped) timer = setTimeout(tick, intervalMs)
        }
    }

    timer = setTimeout(tick, intervalMs)

    return {
        stop: () => { stopped = true; if (timer) clearTimeout(timer) }
    }
}

// ========= Fábrica de cliente =========
export function createLocksClient(resource) {
    const sessionId = getOrCreateSessionIdForResource(resource)

    return {
        resource,
        sessionId,
        ttlMs: DEFAULT_LOCK_TTL_MS,
        acquire: () => acquireLock({ resource, sessionId }),
        renew: () => renewLock({ resource, sessionId }),
        release: () => releaseLock({ resource, sessionId }),
        status: ({ tipo, id }) => statusLock({ tipo, id, sessionId }),
        heartbeat: (handlers = {}) => startLockHeartbeat({ resource, sessionId, ...handlers })
    }
}
